import { Trans, useLingui } from '@lingui/react/macro';
import React, { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { RecorderKind } from 'modules/video-markers/domain/interfaces';
import { MatchDayTips, ObsGuide, OperatorMarkerExplainer, VmixGuide } from 'modules/video-markers/ui/components/guide';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';
import UDTabs from 'ud-ui/components/tabs';
import { Tab } from 'ud-ui/components/tabs/interfaces';
import UDText from 'ud-ui/components/text';

const PROGRAM_GUIDES: { program: RecorderKind; title: string; guide: React.ReactNode }[] = [
  { program: 'obs', title: 'OBS Studio', guide: <ObsGuide /> },
  { program: 'vmix', title: 'vMix', guide: <VmixGuide /> },
];

const PROGRAM_TABS: Tab[] = PROGRAM_GUIDES.map(({ program, title }) => ({ hash: program, label: title }));

/**
 * Инструкция оператора: как подключить табло к OBS или vMix, чтобы на каждое событие матча
 * ставилась видеометка. Живёт рядом с «Устройствами», где копируют ссылку на табло.
 *
 * Оператор работает в одной программе, поэтому инструкции — вкладки, а не две подряд. Вкладка —
 * сегмент адреса (`/devices/video-markers/vmix`): выбор переживает F5 и пересылку ссылки. Ряд
 * вкладок стоит выше вложенного `<Routes>` — `UdTab` навигирует относительно.
 */
function VideoMarkersGuidePage() {
  const { t } = useLingui();
  const { setHeaderContent } = useMainLayout();

  useEffect(() => {
    setHeaderContent(
      <UDBreadcrumbs
        className="my-10"
        breadcrumbs={[
          { label: t`Трансляции`, to: Pages.TEMPLATE.INDEX },
          { label: t`Устройства`, to: Pages.DEVICES.INDEX },
          { label: t`Видеометки`, to: Pages.DEVICES.VIDEO_MARKERS_GUIDE },
        ]}
      />,
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent, t]);

  return (
    <div className="flex w-full flex-col gap-6 px-6 py-6">
      <div className="bg-surfaceCard rounded-card flex flex-col gap-3 p-5">
        <UDText as="h1" variant="title-4">
          <Trans>Видеометки: настройка OBS и vMix</Trans>
        </UDText>
        <UDText variant="body-2">
          <Trans>
            Табло, открытое в OBS или vMix, на каждое событие матча запоминает, на какой секунде записи оно случилось. По этим меткам
            потом нарезаются моменты матча. Настройка делается один раз на компьютере трансляции.
          </Trans>
        </UDText>
      </div>

      <OperatorMarkerExplainer />

      <MatchDayTips />

      <div className="flex flex-col gap-3">
        <UDText variant="body-2" className="text-inkSecondary">
          <Trans>Выберите программу, в которой ведёте трансляцию, и пройдите шаги по порядку.</Trans>
        </UDText>
        <UDTabs tabs={PROGRAM_TABS} defaultTab={PROGRAM_TABS[0]} activeRoutePattern={Pages.DEVICES.VIDEO_MARKERS_GUIDE} />
      </div>

      <Routes>
        {PROGRAM_GUIDES.map(({ program, title, guide }) => (
          <Route
            key={program}
            path={program}
            element={
              <section className="flex flex-col gap-4" aria-labelledby={`video-markers-guide-${program}-title`}>
                <UDText as="h2" variant="title-4" id={`video-markers-guide-${program}-title`}>
                  {title}
                </UDText>
                {guide}
              </section>
            }
          />
        ))}
        <Route path="*" element={<Navigate to="obs" replace />} />
      </Routes>

      <div className="bg-surfaceCard rounded-card flex flex-col gap-2 p-5">
        <UDText as="h2" variant="title-4">
          <Trans>Если что-то не так</Trans>
        </UDText>
        <UDText variant="body-2">
          <Trans>
            Панели видеометок не видно — ссылка скопирована с галочкой «без панели управления». Метки при этом пишутся, панель нужна
            только для проверки.
          </Trans>
        </UDText>
        <UDText variant="body-2">
          <Trans>
            vMix пишет «Нет подключения к vMix», хотя vMix запущен, — не снята галочка повышенной безопасности (шаг 1 vMix) или табло
            добавлено раньше, чем её сняли: закройте инпут табло и добавьте его заново.
          </Trans>
        </UDText>
        <UDText variant="body-2">
          <Trans>Ссылку с паролем OBS не пересылайте посторонним — пароль хранится прямо в ней.</Trans>
        </UDText>
      </div>
    </div>
  );
}

export default VideoMarkersGuidePage;
